/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AddTablePathParameters,
  AddTablesPathParameters,
  FindReuseCandidatesPathParameters,
  PreviewFilterPathParameters,
  RunQueryPathParameters,
  SearchTablesPathParameters,
  SuggestMappingPathParameters,
  TLBulkActionResponseNexusAddTableResult,
  TLHTTPValidationError,
  TLNexusAddTableRequest,
  TLNexusAddTableResponse,
  TLNexusAddTablesRequest,
  TLNexusFilterPreviewRequest,
  TLNexusFilterPreviewResponse,
  TLNexusMappingSuggestionResponse,
  TLNexusQueryResultResponse,
  TLNexusReuseCandidatesRequest,
  TLNexusReuseCandidatesResponse,
  TLNexusRunQueryRequest,
  TLNexusSearchTablesRequest,
  TLNexusSearchTablesResponse,
  TLNexusSuggestMappingRequest,
  TLNexusValidateMappingRequest,
  TLNexusValidateMappingResponse,
  TLNexusValidateSqlRequest,
  TLNexusValidateSqlResponse,
  ValidateMappingPathParameters,
  ValidateSqlPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Preview Filter
 */
export const previewFilter = (
  { projectId }: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusFilterPreviewResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/preview-filter`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusFilterPreviewRequest,
      signal,
    },
    options,
  );
};

export const getPreviewFilterQueryKey = (
  { projectId }: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest?: BodyType<TLNexusFilterPreviewRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'preview-filter', tLNexusFilterPreviewRequest] as const;
};

export const getPreviewFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof previewFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewFilterQueryKey({ projectId }, tLNexusFilterPreviewRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewFilter>>> = ({ signal }) =>
    previewFilter({ projectId }, tLNexusFilterPreviewRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PreviewFilterQueryResult = NonNullable<Awaited<ReturnType<typeof previewFilter>>>;
export type PreviewFilterQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewFilter<
  TData = Awaited<ReturnType<typeof previewFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewFilter>>,
          TError,
          Awaited<ReturnType<typeof previewFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewFilter<
  TData = Awaited<ReturnType<typeof previewFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewFilter>>,
          TError,
          Awaited<ReturnType<typeof previewFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewFilter<
  TData = Awaited<ReturnType<typeof previewFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Filter
 */

export function usePreviewFilter<
  TData = Awaited<ReturnType<typeof previewFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewFilterQueryOptions({ projectId }, tLNexusFilterPreviewRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Filter
 */
export const prefetchPreviewFilterQuery = async <
  TData = Awaited<ReturnType<typeof previewFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewFilterQueryOptions({ projectId }, tLNexusFilterPreviewRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Filter
 */
export const invalidatePreviewFilter = async (
  queryClient: QueryClient,
  { projectId }: PreviewFilterPathParameters,
  tLNexusFilterPreviewRequest: BodyType<TLNexusFilterPreviewRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPreviewFilterQueryKey({ projectId }, tLNexusFilterPreviewRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Run Query
 */
export const runQuery = (
  { projectId }: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusQueryResultResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/query`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusRunQueryRequest,
      signal,
    },
    options,
  );
};

export const getRunQueryQueryKey = (
  { projectId }: RunQueryPathParameters,
  tLNexusRunQueryRequest?: BodyType<TLNexusRunQueryRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'query', tLNexusRunQueryRequest] as const;
};

export const getRunQueryQueryOptions = <
  TData = Awaited<ReturnType<typeof runQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRunQueryQueryKey({ projectId }, tLNexusRunQueryRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof runQuery>>> = ({ signal }) =>
    runQuery({ projectId }, tLNexusRunQueryRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RunQueryQueryResult = NonNullable<Awaited<ReturnType<typeof runQuery>>>;
export type RunQueryQueryError = ErrorType<TLHTTPValidationError>;

export function useRunQuery<TData = Awaited<ReturnType<typeof runQuery>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof runQuery>>, TError, Awaited<ReturnType<typeof runQuery>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunQuery<TData = Awaited<ReturnType<typeof runQuery>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof runQuery>>, TError, Awaited<ReturnType<typeof runQuery>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunQuery<TData = Awaited<ReturnType<typeof runQuery>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Run Query
 */

export function useRunQuery<TData = Awaited<ReturnType<typeof runQuery>>, TError = ErrorType<TLHTTPValidationError>>(
  { projectId }: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRunQueryQueryOptions({ projectId }, tLNexusRunQueryRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Run Query
 */
export const prefetchRunQueryQuery = async <
  TData = Awaited<ReturnType<typeof runQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRunQueryQueryOptions({ projectId }, tLNexusRunQueryRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Run Query
 */
export const invalidateRunQuery = async (
  queryClient: QueryClient,
  { projectId }: RunQueryPathParameters,
  tLNexusRunQueryRequest: BodyType<TLNexusRunQueryRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRunQueryQueryKey({ projectId }, tLNexusRunQueryRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Find Reuse Candidates
 */
export const findReuseCandidates = (
  { projectId }: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusReuseCandidatesResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/reuse-candidates`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusReuseCandidatesRequest,
      signal,
    },
    options,
  );
};

export const getFindReuseCandidatesQueryKey = (
  { projectId }: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest?: BodyType<TLNexusReuseCandidatesRequest>,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'discover',
    'reuse-candidates',
    tLNexusReuseCandidatesRequest,
  ] as const;
};

export const getFindReuseCandidatesQueryOptions = <
  TData = Awaited<ReturnType<typeof findReuseCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getFindReuseCandidatesQueryKey({ projectId }, tLNexusReuseCandidatesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findReuseCandidates>>> = ({ signal }) =>
    findReuseCandidates({ projectId }, tLNexusReuseCandidatesRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type FindReuseCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof findReuseCandidates>>>;
export type FindReuseCandidatesQueryError = ErrorType<TLHTTPValidationError>;

export function useFindReuseCandidates<
  TData = Awaited<ReturnType<typeof findReuseCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findReuseCandidates>>,
          TError,
          Awaited<ReturnType<typeof findReuseCandidates>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindReuseCandidates<
  TData = Awaited<ReturnType<typeof findReuseCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findReuseCandidates>>,
          TError,
          Awaited<ReturnType<typeof findReuseCandidates>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindReuseCandidates<
  TData = Awaited<ReturnType<typeof findReuseCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Find Reuse Candidates
 */

export function useFindReuseCandidates<
  TData = Awaited<ReturnType<typeof findReuseCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindReuseCandidatesQueryOptions({ projectId }, tLNexusReuseCandidatesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Find Reuse Candidates
 */
export const prefetchFindReuseCandidatesQuery = async <
  TData = Awaited<ReturnType<typeof findReuseCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findReuseCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getFindReuseCandidatesQueryOptions({ projectId }, tLNexusReuseCandidatesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Find Reuse Candidates
 */
export const invalidateFindReuseCandidates = async (
  queryClient: QueryClient,
  { projectId }: FindReuseCandidatesPathParameters,
  tLNexusReuseCandidatesRequest: BodyType<TLNexusReuseCandidatesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getFindReuseCandidatesQueryKey({ projectId }, tLNexusReuseCandidatesRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Search Tables
 */
export const searchTables = (
  { projectId }: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusSearchTablesResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/search`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusSearchTablesRequest,
      signal,
    },
    options,
  );
};

export const getSearchTablesQueryKey = (
  { projectId }: SearchTablesPathParameters,
  tLNexusSearchTablesRequest?: BodyType<TLNexusSearchTablesRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'search', tLNexusSearchTablesRequest] as const;
};

export const getSearchTablesQueryOptions = <
  TData = Awaited<ReturnType<typeof searchTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchTablesQueryKey({ projectId }, tLNexusSearchTablesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchTables>>> = ({ signal }) =>
    searchTables({ projectId }, tLNexusSearchTablesRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SearchTablesQueryResult = NonNullable<Awaited<ReturnType<typeof searchTables>>>;
export type SearchTablesQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchTables<
  TData = Awaited<ReturnType<typeof searchTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchTables>>,
          TError,
          Awaited<ReturnType<typeof searchTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchTables<
  TData = Awaited<ReturnType<typeof searchTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchTables>>,
          TError,
          Awaited<ReturnType<typeof searchTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchTables<
  TData = Awaited<ReturnType<typeof searchTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Tables
 */

export function useSearchTables<
  TData = Awaited<ReturnType<typeof searchTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchTablesQueryOptions({ projectId }, tLNexusSearchTablesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Tables
 */
export const prefetchSearchTablesQuery = async <
  TData = Awaited<ReturnType<typeof searchTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchTablesQueryOptions({ projectId }, tLNexusSearchTablesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Tables
 */
export const invalidateSearchTables = async (
  queryClient: QueryClient,
  { projectId }: SearchTablesPathParameters,
  tLNexusSearchTablesRequest: BodyType<TLNexusSearchTablesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSearchTablesQueryKey({ projectId }, tLNexusSearchTablesRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Suggest Mapping
 */
export const suggestMapping = (
  { projectId }: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusMappingSuggestionResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/suggest-mapping`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusSuggestMappingRequest,
      signal,
    },
    options,
  );
};

export const getSuggestMappingQueryKey = (
  { projectId }: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest?: BodyType<TLNexusSuggestMappingRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'suggest-mapping', tLNexusSuggestMappingRequest] as const;
};

export const getSuggestMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof suggestMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSuggestMappingQueryKey({ projectId }, tLNexusSuggestMappingRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof suggestMapping>>> = ({ signal }) =>
    suggestMapping({ projectId }, tLNexusSuggestMappingRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SuggestMappingQueryResult = NonNullable<Awaited<ReturnType<typeof suggestMapping>>>;
export type SuggestMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useSuggestMapping<
  TData = Awaited<ReturnType<typeof suggestMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof suggestMapping>>,
          TError,
          Awaited<ReturnType<typeof suggestMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSuggestMapping<
  TData = Awaited<ReturnType<typeof suggestMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof suggestMapping>>,
          TError,
          Awaited<ReturnType<typeof suggestMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSuggestMapping<
  TData = Awaited<ReturnType<typeof suggestMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Suggest Mapping
 */

export function useSuggestMapping<
  TData = Awaited<ReturnType<typeof suggestMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSuggestMappingQueryOptions({ projectId }, tLNexusSuggestMappingRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Suggest Mapping
 */
export const prefetchSuggestMappingQuery = async <
  TData = Awaited<ReturnType<typeof suggestMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof suggestMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSuggestMappingQueryOptions({ projectId }, tLNexusSuggestMappingRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Suggest Mapping
 */
export const invalidateSuggestMapping = async (
  queryClient: QueryClient,
  { projectId }: SuggestMappingPathParameters,
  tLNexusSuggestMappingRequest: BodyType<TLNexusSuggestMappingRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSuggestMappingQueryKey({ projectId }, tLNexusSuggestMappingRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Add Table
 */
export const addTable = (
  { projectId }: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusAddTableResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/table`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusAddTableRequest,
      signal,
    },
    options,
  );
};

export const getAddTableQueryKey = (
  { projectId }: AddTablePathParameters,
  tLNexusAddTableRequest?: BodyType<TLNexusAddTableRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'table', tLNexusAddTableRequest] as const;
};

export const getAddTableQueryOptions = <
  TData = Awaited<ReturnType<typeof addTable>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddTableQueryKey({ projectId }, tLNexusAddTableRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addTable>>> = ({ signal }) =>
    addTable({ projectId }, tLNexusAddTableRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddTableQueryResult = NonNullable<Awaited<ReturnType<typeof addTable>>>;
export type AddTableQueryError = ErrorType<TLHTTPValidationError>;

export function useAddTable<TData = Awaited<ReturnType<typeof addTable>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof addTable>>, TError, Awaited<ReturnType<typeof addTable>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddTable<TData = Awaited<ReturnType<typeof addTable>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof addTable>>, TError, Awaited<ReturnType<typeof addTable>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddTable<TData = Awaited<ReturnType<typeof addTable>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Table
 */

export function useAddTable<TData = Awaited<ReturnType<typeof addTable>>, TError = ErrorType<TLHTTPValidationError>>(
  { projectId }: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddTableQueryOptions({ projectId }, tLNexusAddTableRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Table
 */
export const prefetchAddTableQuery = async <
  TData = Awaited<ReturnType<typeof addTable>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTable>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddTableQueryOptions({ projectId }, tLNexusAddTableRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Table
 */
export const invalidateAddTable = async (
  queryClient: QueryClient,
  { projectId }: AddTablePathParameters,
  tLNexusAddTableRequest: BodyType<TLNexusAddTableRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddTableQueryKey({ projectId }, tLNexusAddTableRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Add Tables
 */
export const addTables = (
  { projectId }: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkActionResponseNexusAddTableResult>(
    {
      url: `/nexus/projects/${projectId}/discover/tables`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusAddTablesRequest,
      signal,
    },
    options,
  );
};

export const getAddTablesQueryKey = (
  { projectId }: AddTablesPathParameters,
  tLNexusAddTablesRequest?: BodyType<TLNexusAddTablesRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'tables', tLNexusAddTablesRequest] as const;
};

export const getAddTablesQueryOptions = <
  TData = Awaited<ReturnType<typeof addTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddTablesQueryKey({ projectId }, tLNexusAddTablesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addTables>>> = ({ signal }) =>
    addTables({ projectId }, tLNexusAddTablesRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddTablesQueryResult = NonNullable<Awaited<ReturnType<typeof addTables>>>;
export type AddTablesQueryError = ErrorType<TLHTTPValidationError>;

export function useAddTables<TData = Awaited<ReturnType<typeof addTables>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof addTables>>, TError, Awaited<ReturnType<typeof addTables>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddTables<TData = Awaited<ReturnType<typeof addTables>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addTables>>,
          TError,
          Awaited<ReturnType<typeof addTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddTables<TData = Awaited<ReturnType<typeof addTables>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Tables
 */

export function useAddTables<TData = Awaited<ReturnType<typeof addTables>>, TError = ErrorType<TLHTTPValidationError>>(
  { projectId }: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddTablesQueryOptions({ projectId }, tLNexusAddTablesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Tables
 */
export const prefetchAddTablesQuery = async <
  TData = Awaited<ReturnType<typeof addTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddTablesQueryOptions({ projectId }, tLNexusAddTablesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Tables
 */
export const invalidateAddTables = async (
  queryClient: QueryClient,
  { projectId }: AddTablesPathParameters,
  tLNexusAddTablesRequest: BodyType<TLNexusAddTablesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddTablesQueryKey({ projectId }, tLNexusAddTablesRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Validate Mapping
 */
export const validateMapping = (
  { projectId }: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusValidateMappingResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/validate-mapping`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusValidateMappingRequest,
      signal,
    },
    options,
  );
};

export const getValidateMappingQueryKey = (
  { projectId }: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest?: BodyType<TLNexusValidateMappingRequest>,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'discover',
    'validate-mapping',
    tLNexusValidateMappingRequest,
  ] as const;
};

export const getValidateMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof validateMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getValidateMappingQueryKey({ projectId }, tLNexusValidateMappingRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof validateMapping>>> = ({ signal }) =>
    validateMapping({ projectId }, tLNexusValidateMappingRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ValidateMappingQueryResult = NonNullable<Awaited<ReturnType<typeof validateMapping>>>;
export type ValidateMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useValidateMapping<
  TData = Awaited<ReturnType<typeof validateMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateMapping>>,
          TError,
          Awaited<ReturnType<typeof validateMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateMapping<
  TData = Awaited<ReturnType<typeof validateMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateMapping>>,
          TError,
          Awaited<ReturnType<typeof validateMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateMapping<
  TData = Awaited<ReturnType<typeof validateMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Validate Mapping
 */

export function useValidateMapping<
  TData = Awaited<ReturnType<typeof validateMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getValidateMappingQueryOptions({ projectId }, tLNexusValidateMappingRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Validate Mapping
 */
export const prefetchValidateMappingQuery = async <
  TData = Awaited<ReturnType<typeof validateMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getValidateMappingQueryOptions({ projectId }, tLNexusValidateMappingRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Validate Mapping
 */
export const invalidateValidateMapping = async (
  queryClient: QueryClient,
  { projectId }: ValidateMappingPathParameters,
  tLNexusValidateMappingRequest: BodyType<TLNexusValidateMappingRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getValidateMappingQueryKey({ projectId }, tLNexusValidateMappingRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Validate Sql
 */
export const validateSql = (
  { projectId }: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusValidateSqlResponse>(
    {
      url: `/nexus/projects/${projectId}/discover/validate-sql`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusValidateSqlRequest,
      signal,
    },
    options,
  );
};

export const getValidateSqlQueryKey = (
  { projectId }: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest?: BodyType<TLNexusValidateSqlRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'discover', 'validate-sql', tLNexusValidateSqlRequest] as const;
};

export const getValidateSqlQueryOptions = <
  TData = Awaited<ReturnType<typeof validateSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getValidateSqlQueryKey({ projectId }, tLNexusValidateSqlRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof validateSql>>> = ({ signal }) =>
    validateSql({ projectId }, tLNexusValidateSqlRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ValidateSqlQueryResult = NonNullable<Awaited<ReturnType<typeof validateSql>>>;
export type ValidateSqlQueryError = ErrorType<TLHTTPValidationError>;

export function useValidateSql<
  TData = Awaited<ReturnType<typeof validateSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateSql>>,
          TError,
          Awaited<ReturnType<typeof validateSql>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateSql<
  TData = Awaited<ReturnType<typeof validateSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateSql>>,
          TError,
          Awaited<ReturnType<typeof validateSql>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateSql<
  TData = Awaited<ReturnType<typeof validateSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Validate Sql
 */

export function useValidateSql<
  TData = Awaited<ReturnType<typeof validateSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getValidateSqlQueryOptions({ projectId }, tLNexusValidateSqlRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Validate Sql
 */
export const prefetchValidateSqlQuery = async <
  TData = Awaited<ReturnType<typeof validateSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getValidateSqlQueryOptions({ projectId }, tLNexusValidateSqlRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Validate Sql
 */
export const invalidateValidateSql = async (
  queryClient: QueryClient,
  { projectId }: ValidateSqlPathParameters,
  tLNexusValidateSqlRequest: BodyType<TLNexusValidateSqlRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getValidateSqlQueryKey({ projectId }, tLNexusValidateSqlRequest) },
    options,
  );

  return queryClient;
};
